import { InputField } from '@atoms/input';
import BlockBase from '@components/dnd/base/blockBase';
import { Column } from '@components/dnd/base/blockBase/body.block.style';
import { useAtom } from '@hooks/useAtom';
import useProcess from '@hooks/useProcess';
import EndControlsFB from '@library/formBuilder/endControls';
import { FormControl, FormLabel, Radio, RadioGroup, Textarea } from '@mui/joy';
import {
  ALL_TYPE_KIND,
  type AllKindOfType,
  type DNDBlock,
  type PrimaryType,
  ResolvedVariableType,
  StaticType
} from '@stratumn/studio-shared';
import { type LucideIcon, Variable } from 'lucide-react';
import { observer } from 'mobx-react';
import type { ChangeEvent } from 'react';
import { newError } from '@/services/errors/errors';

import { initialGlobalVariableData } from './globalVariable.data';
import {
  isCorrectTypeKind as isCorrectKindOfType,
  kindToLabelMap,
  primaryTypeToInfoMap
} from './globalVariable.utils';
import { TypeSelector } from './typeSelector';

export const GlobalVariableIcon: LucideIcon = Variable;

export const GlobalVariable = observer((block: DNDBlock) => {
  const process = useProcess();
  const workflow = process?.workflows[0];
  if (!workflow) {
    return null;
  }

  const atom = useAtom({
    type: 'GlobalVariable',
    sourceId: block.id,
    parentId: workflow.id,
    parentKind: 'globalVariables',
    initialData: initialGlobalVariableData,
    variableInfo: {
      resolvedType: ResolvedVariableType.Text
    }
  });

  if (!atom) {
    return null;
  }

  const onTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    atom.data.title = event.target.value;
  };

  const onKindOfTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!isCorrectKindOfType(e.target.value)) {
      newError('GV-1000', `Unknown kind: ${e.target.value}`, true);
      return;
    }

    atom.data.variableType.selectedKind = e.target.value;

    reCalculateResolvedType(
      atom.data.variableType.selectedKind,
      atom.data.variableType.type
    );
  };

  const getSettingsModal = () => ({
    title: atom.data.title ?? 'Global variable',
    content: <></>
  });

  const onTypeChange = (type: PrimaryType): void => {
    const kind = atom.data.variableType.selectedKind;
    if (kind === 'custom' || !atom.variableInfo) {
      return;
    }

    atom.data.variableType.type = type;

    reCalculateResolvedType(kind, type);
  };

  const reCalculateResolvedType = (
    _kind?: AllKindOfType,
    _type?: PrimaryType
  ): void => {
    if (!atom.variableInfo?.resolvedType) {
      return;
    }
    const { selectedKind, type: variableType } = atom.data.variableType;
    const kind = _kind ?? selectedKind;
    const type = _type ?? variableType;

    switch (kind) {
      case 'custom':
        atom.variableInfo.resolvedType = ResolvedVariableType.Custom;
        break;
      case 'normal':
        atom.variableInfo.resolvedType =
          primaryTypeToInfoMap[type].resolvedType;
        break;
      case 'list': {
        if (type === StaticType.FormFile) {
          atom.variableInfo.resolvedType =
            primaryTypeToInfoMap[StaticType.FormFile].resolvedType; // this is really bad
          break;
        }
        atom.variableInfo.resolvedType = ResolvedVariableType.List;
        break;
      }
      default:
        newError('GV-2000', `Unknown kind: ${JSON.stringify(kind)}`, true);
    }
  };

  const onCustomTypeChange = (customType: string): void => {
    if (!atom.variableInfo?.resolvedType) {
      return;
    }
    atom.data.variableType.customType = customType;
    atom.variableInfo.resolvedType = ResolvedVariableType.Custom;
  };

  return (
    <BlockBase
      dataItem={atom}
      dndBlock={block}
      endControls={
        <EndControlsFB
          dataItem={atom}
          dndBlock={block}
          hasRequired={false}
          settings={getSettingsModal()}
        />
      }
      icon={GlobalVariableIcon}
      title={atom.data.title}
    >
      <Column $width="200px">
        <InputField
          label="Variable title"
          onChange={onTitleChange}
          placeholder="Aa"
          required
          value={atom.data.title}
        />
      </Column>
      <Column $width="100px">
        <FormControl size="sm">
          <FormLabel>Kind</FormLabel>
          <RadioGroup
            name="radio-buttons-group"
            value={atom.data.variableType.selectedKind}
          >
            {ALL_TYPE_KIND.map(kind => (
              <Radio
                color="neutral"
                key={kind}
                label={kindToLabelMap[kind]}
                onChange={onKindOfTypeChange}
                size="sm"
                value={kind}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Column>
      <TypeSelector
        {...atom.data.variableType}
        onCustomTypeChange={onCustomTypeChange}
        onTypeChange={onTypeChange}
      />
      <Column $width="100%">
        <FormControl size="sm">
          <FormLabel>Description</FormLabel>
          <Textarea
            minRows={2}
            onChange={e => {
              atom.data.description = e.target.value;
            }}
            placeholder={`${
              atom.data.title
                ? `The variable "${atom.traceKey.value}"`
                : 'This variable'
            } represents...`}
            value={atom.data.description}
          />
        </FormControl>
      </Column>
    </BlockBase>
  );
});
